import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  getTrackSimilarity,
  getTrackSimilarityWeights,
  TrackSimilarityMatrix,
  TrackSimilarityPair,
  TrackSimilarityTrack,
  TrackWeightsResult,
} from '../services/api';

type Family = 'oval' | 'road' | 'drafting' | 'all';
type Metric = 'predictive' | 'character' | 'specific';

const FAMILY_LABELS: Record<Family, string> = { oval: 'Ovals', road: 'Road courses', drafting: 'Drafting', all: 'All' };
const TYPE_ORDER = ['superspeedway', 'intermediate', 'large_oval', 'mile', 'short', 'road'];
const formatType = (t: string) => t.replace(/_/g, ' ');

// Sequential single-hue (blue) ramp for similarity, interpolated between validated reference steps. Every cell is a
// real value, so every step clears 2:1 against the card surface (light #ffffff: step 250 -> 700; dark #1e1e1e:
// step 150 -> 550). Darker = more similar in both themes. Values outside a metric's range render at the ends.
// Track-specific correlations are much smaller (~0 = no link, 0.2+ = strong), so that view has its own range.
const SCALES: Record<Metric, { lo: number; hi: number; loLabel: string; hiLabel: string }> = {
  predictive: { lo: 0.4, hi: 1, loLabel: '≤0.4', hiLabel: '1.0' },
  character: { lo: 0.4, hi: 1, loLabel: '≤0.4', hiLabel: '1.0' },
  specific: { lo: 0, hi: 0.2, loLabel: '≤0 (no link)', hiLabel: '0.20+' },
};
const RAMP_LIGHT = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#0d366b'];
const RAMP_DARK = ['#b7d3f6', '#86b6ef', '#5598e7', '#2a78d6', '#1c5cab'];

const hexToRgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const rampColor = (value: number, ramp: string[], lo: number, hi: number): string => {
  const t = Math.max(0, Math.min(1, (value - lo) / (hi - lo))) * (ramp.length - 1);
  const i = Math.min(ramp.length - 2, Math.floor(t));
  const f = t - i;
  const a = hexToRgb(ramp[i]);
  const b = hexToRgb(ramp[i + 1]);
  return `rgb(${a.map((v, k) => Math.round(v + (b[k] - v) * f)).join(',')})`;
};

interface Candidate {
  track: TrackSimilarityTrack;
  value: number;
  nRacePairs: number;
  se: number | null; // track-specific only
  predictive: number;
}

// A track-specific link counts as clear when it's more than two standard errors above zero.
const isClearLink = (value: number, se: number | null) => se !== null && value > 2 * se;

/** The selected track's similarities, each candidate with a bar, evidence, and whether the simulator uses it. */
const TrackDetail: React.FC<{
  track: TrackSimilarityTrack;
  candidates: Candidate[];
  metric: Metric;
}> = ({ track, candidates, metric }) => {
  const weights = useQuery<TrackWeightsResult, Error>({
    queryKey: ['trackSimilarityWeights', track.site],
    queryFn: () => getTrackSimilarityWeights(track.site!),
    enabled: !!track.site,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const weightByTrack = useMemo(() => {
    const m = new Map<string, number>();
    // Under the two-layer model every family track feeds form equally, so "Used" marks the adjustment's links.
    if (!weights.data?.twoLayer) weights.data?.weights.forEach((w) => m.set(w.trackKey, w.weight));
    return m;
  }, [weights.data]);
  const linkedTracks = useMemo(
    () => new Set(weights.data?.twoLayer ? weights.data.linkedTracks.map((t) => t.trackKey) : []),
    [weights.data]
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">{track.trackKey}</Typography>
        <Chip size="small" label={formatType(track.trackType)} variant="outlined" />
      </Stack>
      <Tooltip title="How consistent results here are from year to year (correlation of the same drivers' results a year apart). Higher = history is more predictive.">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: 'inline-block' }}>
          Self-consistency: <strong>{track.selfConsistency.toFixed(2)}</strong>
        </Typography>
      </Tooltip>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {metric === 'predictive'
          ? `Best predictors of ${track.trackKey}`
          : metric === 'character'
            ? `Most alike ${track.trackKey}`
            : `Track-specific matches for ${track.trackKey}`}
      </Typography>
      {metric === 'specific' && track.selfSpecificR !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {track.trackKey} with itself (other races): <strong>{track.selfSpecificR.toFixed(2)}</strong>
          {track.selfSpecificSE !== null && ` ± ${track.selfSpecificSE.toFixed(2)}`}. How repeatable track-specific
          skill is here; a match near this value is as informative as {track.trackKey}'s own history.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {track.site
          ? weights.isLoading
            ? 'Loading which tracks the simulator uses…'
            : weights.data
              ? weights.data.twoLayer
                ? `The simulator (config ${weights.data.configName}) rates form from every track in this family equally; "Track-specific link" = also feeds ${track.trackKey}'s track-specific adjustment.`
                : `"Used" = feeds the simulator for ${track.trackKey} today (config ${weights.data.configName}), with its weight.`
              : 'Couldn’t load the simulator’s weights.'
          : 'Not on the current schedule, so the simulator doesn’t run this track.'}
      </Typography>
      <Stack spacing={1.25}>
        {candidates.map((c) => {
          const w = weightByTrack.get(c.track.trackKey);
          return (
            <Box key={c.track.trackKey}>
              {/* Name and value on the first line; chips on their own line so they never truncate the name. */}
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {c.track.trackKey}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {c.value.toFixed(2)}
                  {metric === 'specific' && c.se !== null && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}± {c.se.toFixed(2)}
                    </Typography>
                  )}
                </Typography>
              </Stack>
              {(w !== undefined || metric === 'specific' || linkedTracks.has(c.track.trackKey)) && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  {linkedTracks.has(c.track.trackKey) && (
                    <Chip size="small" color="primary" label="Track-specific link" sx={{ height: 20 }} />
                  )}
                  {w !== undefined && (
                    <Chip size="small" color="primary" label={`Used · ${w.toFixed(2)}`} sx={{ height: 20 }} />
                  )}
                  {metric === 'specific' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isClearLink(c.value, c.se) ? 'Clear link' : 'No clear link'}
                      sx={{ height: 20, opacity: isClearLink(c.value, c.se) ? 1 : 0.7 }}
                    />
                  )}
                </Stack>
              )}
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, (c.value / (metric === 'specific' ? 0.25 : 1)) * 100))}
                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatType(c.track.trackType)}
                {metric === 'specific' && ` · Predictive ${c.predictive.toFixed(2)}`} ·{' '}
                <Tooltip title={`${c.nRacePairs} race pair${c.nRacePairs === 1 ? '' : 's'} of evidence: races at the two tracks run by the same drivers, compared with each other`}>
                  <span>
                    {c.nRacePairs} pair{c.nRacePairs === 1 ? '' : 's'}
                  </span>
                </Tooltip>
              </Typography>
            </Box>
          );
        })}
        {candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No comparable tracks in this view.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

interface MetricExample {
  a: TrackSimilarityTrack; // the track that's predicted better
  b: TrackSimilarityTrack;
  aFromB: number; // predictive: B's races used for A
  bFromA: number; // predictive: A's races used for B
  character: number;
}

interface SpecificExample {
  target: TrackSimilarityTrack;
  lookalikes: Candidate[]; // high Predictive score but a track-specific link near zero
  best: Candidate | null; // strongest clear track-specific link
}

/** Plain-language description of the selected score, with a live example from the data in view. */
const MetricExplainer: React.FC<{
  metric: Metric;
  example: MetricExample | null;
  specificExample: SpecificExample | null;
}> = ({ metric, example, specificExample }) => {
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 1100 }}>
      {metric === 'predictive' ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Predictive: "if I'm forecasting the row track, how much is a race at the column track worth?"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1.00 means a race there is as useful as a race at the row track itself; 0.50 means it's worth half a race.
            It's one-way: a track whose results are steady year to year is a good predictor of others, while a track
            with a lot of randomness says less about anywhere else. <strong>This is the score the Simulator uses</strong>{' '}
            to decide which tracks feed a simulation and how much each counts. Much of it is general driver strength
            (good drivers run well almost everywhere), which is why even unlike ovals score 0.8+; switch to{' '}
            <strong>Track-specific</strong> to see what's left once that's removed.
          </Typography>
        </>
      ) : metric === 'specific' ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Track-specific: "does a driver who runs better than usual at one track also run better than usual at the
            other?"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            First we take out each driver's general strength (their average across all tracks around that time), so a
            great driver finishing well everywhere no longer counts. What's left is track-specific skill. 0 means no
            link; 0.1 is a real link; 0.2 is about as strong as a track's link with its own past races. These are the
            "similar tracks" a betting analyst means. "Clear link" means more than two standard errors above zero. The Simulator's
            <strong>Track Specialists</strong> model uses the clear links.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Character: "do these two tracks reward the same drivers?"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One two-way score per pair, the same in both directions. It takes out each track's own randomness, so it
            measures how alike the tracks behave rather than how much one helps forecast the other. Good for spotting
            track groups (e.g. which short tracks race alike). The Simulator doesn't use it.
          </Typography>
        </>
      )}
      {metric === 'specific' && specificExample && (specificExample.lookalikes.length > 0 || specificExample.best) && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <strong>Example ({specificExample.target.trackKey}):</strong>{' '}
          {specificExample.lookalikes.length > 0 && (
            <>
              {specificExample.lookalikes
                .map((c) => `${c.track.trackKey} (Predictive ${c.predictive.toFixed(2)}, track-specific ${c.value.toFixed(2)})`)
                .join(' and ')}{' '}
              look similar on Predictive but have essentially no track-specific link: their results line up with{' '}
              {specificExample.target.trackKey}'s mostly because good drivers are good everywhere.{' '}
            </>
          )}
          {specificExample.best && (
            <>
              The strongest track-specific match is {specificExample.best.track.trackKey} (
              {specificExample.best.value.toFixed(2)}
              {specificExample.best.se !== null && ` ± ${specificExample.best.se.toFixed(2)}`}).
            </>
          )}
        </Typography>
      )}
      {metric !== 'specific' && example && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <strong>Example:</strong> a race at {example.b.trackKey} is worth {pct(example.aFromB)} of a race at{' '}
          {example.a.trackKey} for predicting {example.a.trackKey}, but a race at {example.a.trackKey} is worth only{' '}
          {pct(example.bFromA)} for predicting {example.b.trackKey}, because results at {example.a.trackKey} are less
          consistent from year to year ({example.a.selfConsistency.toFixed(2)} vs {example.b.selfConsistency.toFixed(2)}).
          Their Character score, one number for the pair, is {example.character.toFixed(2)}.
        </Typography>
      )}
    </Paper>
  );
};

export const TrackSimilarity: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ramp = theme.palette.mode === 'dark' ? RAMP_DARK : RAMP_LIGHT;
  const [metric, setMetric] = useState<Metric>('predictive');
  const scale = SCALES[metric];
  const [family, setFamily] = useState<Family>('oval');
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<TrackSimilarityMatrix, Error>({
    queryKey: ['trackSimilarity'],
    queryFn: getTrackSimilarity,
    staleTime: 30 * 60 * 1000,
  });

  const tracksByKey = useMemo(() => new Map((data?.tracks ?? []).map((t) => [t.trackKey, t])), [data]);
  const pairByKey = useMemo(() => {
    const m = new Map<string, TrackSimilarityPair>();
    (data?.pairs ?? []).forEach((p) => m.set(`${p.trackA}|${p.trackB}`, p));
    return m;
  }, [data]);

  // Tracks in view, grouped by type and ordered by self-consistency within a type.
  const tracks = useMemo(() => {
    const list = (data?.tracks ?? []).filter((t) => family === 'all' || t.family === family);
    return list.sort(
      (a, b) =>
        TYPE_ORDER.indexOf(a.trackType) - TYPE_ORDER.indexOf(b.trackType) ||
        b.selfConsistency - a.selfConsistency ||
        a.trackKey.localeCompare(b.trackKey)
    );
  }, [data, family]);

  useEffect(() => {
    if (tracks.length && (!selected || !tracks.some((t) => t.trackKey === selected))) {
      const preferred = tracks.find((t) => t.trackKey === 'Las Vegas' || t.trackKey === 'Watkins Glen' || t.trackKey === 'Talladega');
      setSelected((preferred ?? tracks[0]).trackKey);
    }
  }, [tracks, selected]);

  const valueOf = (
    a: string,
    b: string
  ): { value: number; nRacePairs: number; se: number | null; predictive: number } | null => {
    const p = pairByKey.get(`${a}|${b}`);
    if (!p) return null;
    if (metric === 'specific') {
      if (p.specificR === null) return null;
      return { value: p.specificR, nRacePairs: p.specificNRacePairs ?? 0, se: p.specificSE, predictive: p.similarity };
    }
    return {
      value: metric === 'predictive' ? p.similarity : p.similaritySymmetric,
      nRacePairs: p.nRacePairs,
      se: null,
      predictive: p.similarity,
    };
  };

  const selectedTrack = selected ? tracksByKey.get(selected) : undefined;
  const candidates = useMemo(() => {
    if (!selectedTrack) return [];
    return tracks
      .filter((t) => t.trackKey !== selectedTrack.trackKey)
      .map((t) => {
        const v = valueOf(selectedTrack.trackKey, t.trackKey);
        return v ? { track: t, ...v } : null;
      })
      .filter((c): c is Candidate => c !== null)
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrack, tracks, metric, pairByKey]);

  // Track-specific example for the selected track: the best-looking Predictive match with no clear track-specific
  // link, and the strongest clear track-specific match.
  const specificExample = useMemo<SpecificExample | null>(() => {
    if (!selectedTrack || metric !== 'specific') return null;
    const evidenced = candidates.filter((c) => c.nRacePairs >= 15);
    // "Near zero": below 0.04 and within one standard error of zero (not just short of a clear link).
    const lookalikes = evidenced
      .filter((c) => c.value < 0.04 && (c.se === null || c.value < c.se) && c.predictive >= 0.75)
      .sort((a, b) => b.predictive - a.predictive)
      .slice(0, 2);
    const best = evidenced.find((c) => isClearLink(c.value, c.se)) ?? null;
    return { target: selectedTrack, lookalikes, best };
  }, [selectedTrack, metric, candidates]);

  // Example for the explainer: the pair in view where the two directions differ most (with real evidence behind it).
  const example = useMemo<MetricExample | null>(() => {
    let best: MetricExample | null = null;
    for (const a of tracks) {
      for (const b of tracks) {
        if (a.trackKey >= b.trackKey) continue;
        const ab = pairByKey.get(`${a.trackKey}|${b.trackKey}`);
        const ba = pairByKey.get(`${b.trackKey}|${a.trackKey}`);
        if (!ab || !ba || ab.nRacePairs < 15) continue;
        const gap = Math.abs(ab.similarity - ba.similarity);
        if (!best || gap > Math.abs(best.aFromB - best.bFromA)) {
          best = { a, b, aFromB: ab.similarity, bFromA: ba.similarity, character: ab.similaritySymmetric };
        }
      }
    }
    // Present it with the better-predicted track first.
    if (best && best.bFromA > best.aFromB) {
      best = { a: best.b, b: best.a, aFromB: best.bFromA, bFromA: best.aFromB, character: best.character };
    }
    return best && best.aFromB - best.bFromA >= 0.1 ? best : null;
  }, [tracks, pairByKey]);

  // Size cells to fill the heatmap card's width (within limits); on narrow screens it scrolls inside the card.
  const heatmapRef = useRef<HTMLDivElement | null>(null);
  const [heatmapWidth, setHeatmapWidth] = useState(0);
  useEffect(() => {
    const el = heatmapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setHeatmapWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);
  const labelWidth = isMobile ? 96 : 132;
  const minCell = isMobile ? 18 : 20;
  const cell = tracks.length
    ? Math.max(minCell, Math.min(44, Math.floor((heatmapWidth - labelWidth - 8) / tracks.length)))
    : minCell;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, px: isMobile ? 2 : 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Track Similarity
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Two tracks are similar when the same drivers do well (or badly) at both: we compare finishes, speed,
        qualifying, passing and laps led, never length or banking.
      </Typography>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={family} onChange={(_, v) => v && setFamily(v)}>
          {(Object.keys(FAMILY_LABELS) as Family[]).map((f) => (
            <ToggleButton key={f} value={f}>
              {FAMILY_LABELS[f]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
          <ToggleButton value="predictive">Predictive</ToggleButton>
          <ToggleButton value="character">Character</ToggleButton>
          <ToggleButton value="specific">Track-specific</ToggleButton>
        </ToggleButtonGroup>
        {data && <Chip label={`Data through ${data.asOf}`} variant="outlined" size="small" sx={{ alignSelf: isMobile ? 'flex-start' : 'center' }} />}
      </Stack>

      {data && <MetricExplainer metric={metric} example={example} specificExample={specificExample} />}

      {isLoading && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Computing track similarity… (this can take up to a minute the first time; after that it’s instant)
          </Typography>
        </Stack>
      )}
      {error && <Alert severity="error">{error.message}</Alert>}

      {data && (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(280px, 1.2fr)',
            alignItems: 'start',
          }}
        >
          <Stack spacing={3} sx={{ minWidth: 0 }}>
            {/* Heatmap: row = track being predicted, column = track whose races are used. */}
            <Paper ref={heatmapRef} sx={{ p: 2, overflowX: 'auto', minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {metric === 'predictive'
                  ? 'How well each column track predicts each row track'
                  : metric === 'character'
                    ? 'How alike each pair of tracks is'
                    : 'Which tracks reward the same drivers beyond general strength'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {isMobile ? 'Tap' : 'Hover'} a cell for the value; {isMobile ? 'tap' : 'click'} a row to see its details.{' '}
                Darker = more similar.
                {metric === 'specific' &&
                  ' Outlined cells on the diagonal are each track with its own other races. Pairs with few race pairs are noisy; the details panel shows ± and whether each link is clear.'}
              </Typography>
              <Box sx={{ display: 'inline-block' }}>
                {/* Column labels */}
                <Box sx={{ display: 'flex', ml: `${labelWidth}px` }}>
                  {tracks.map((col) => (
                    <Box
                      key={col.trackKey}
                      sx={{
                        width: cell,
                        height: isMobile ? 90 : 120,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          fontWeight: col.trackKey === selected ? 700 : 400,
                          color: col.trackKey === selected ? 'text.primary' : 'text.secondary',
                          maxHeight: isMobile ? 88 : 118,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {col.trackKey}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {tracks.map((row) => (
                  <Box
                    key={row.trackKey}
                    onClick={() => setSelected(row.trackKey)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 0.5,
                      outline: row.trackKey === selected ? `2px solid ${theme.palette.primary.main}` : 'none',
                    }}
                  >
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        width: labelWidth,
                        pr: 1,
                        textAlign: 'right',
                        fontWeight: row.trackKey === selected ? 700 : 400,
                        color: row.trackKey === selected ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {row.trackKey}
                    </Typography>
                    {tracks.map((col) => {
                      if (row.trackKey === col.trackKey && metric === 'specific' && row.selfSpecificR !== null) {
                        const selfR = row.selfSpecificR;
                        return (
                          <Tooltip
                            key={col.trackKey}
                            title={`${row.trackKey} with its own other races: ${selfR.toFixed(2)}${row.selfSpecificSE !== null ? ` ± ${row.selfSpecificSE.toFixed(2)}` : ''} (how repeatable track-specific skill is here)`}
                            disableInteractive
                          >
                            <Box sx={{ width: cell, height: cell, p: '1px' }}>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '3px',
                                  bgcolor: rampColor(selfR, ramp, scale.lo, scale.hi),
                                  outline: `1px solid ${theme.palette.text.secondary}`,
                                  outlineOffset: '-1px',
                                }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      }
                      if (row.trackKey === col.trackKey) {
                        return (
                          <Box
                            key={col.trackKey}
                            sx={{ width: cell, height: cell, p: '1px' }}
                          >
                            <Box sx={{ width: '100%', height: '100%', borderRadius: '3px', bgcolor: 'action.selected' }} />
                          </Box>
                        );
                      }
                      const v = valueOf(row.trackKey, col.trackKey);
                      const tip = v
                        ? metric === 'predictive'
                          ? `${col.trackKey} → ${row.trackKey}: ${v.value.toFixed(2)} (a race at ${col.trackKey} is worth ${Math.round(v.value * 100)}% of a race at ${row.trackKey}) · ${v.nRacePairs} race pairs`
                          : metric === 'specific'
                            ? `${row.trackKey} ↔ ${col.trackKey}: track-specific ${v.value.toFixed(2)}${v.se !== null ? ` ± ${v.se.toFixed(2)}` : ''} (${isClearLink(v.value, v.se) ? 'clear link' : 'no clear link'}; Predictive ${v.predictive.toFixed(2)}) · ${v.nRacePairs} race pairs`
                            : `${row.trackKey} ↔ ${col.trackKey}: ${v.value.toFixed(2)} · ${v.nRacePairs} race pairs`
                        : `${row.trackKey} / ${col.trackKey}: not enough overlapping races`;
                      return (
                        <Tooltip key={col.trackKey} title={tip} disableInteractive>
                          <Box sx={{ width: cell, height: cell, p: '1px' }}>
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '3px',
                                bgcolor: v ? rampColor(v.value, ramp, scale.lo, scale.hi) : 'transparent',
                                border: v ? 'none' : `1px dashed ${theme.palette.divider}`,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
                {/* Legend */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, ml: `${labelWidth}px` }}>
                  <Typography variant="caption" color="text.secondary">
                    {scale.loLabel}
                  </Typography>
                  <Box
                    sx={{
                      width: 160,
                      height: 10,
                      borderRadius: 5,
                      background: `linear-gradient(to right, ${ramp.join(',')})`,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {scale.hiLabel}
                  </Typography>
                  <Box sx={{ width: 12, height: 12, border: `1px dashed ${theme.palette.divider}`, borderRadius: '3px', ml: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    not enough data
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            {/* Self-consistency ranking */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                How predictable each track is
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Self-consistency: how similar the same drivers' results are at a track a year apart. Low values (e.g.
                superspeedways) mean history says less about the next race.
              </Typography>
              <Stack spacing={0.75}>
                {[...tracks]
                  .sort((a, b) => b.selfConsistency - a.selfConsistency)
                  .map((t) => (
                    <Stack
                      key={t.trackKey}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() => setSelected(t.trackKey)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ width: labelWidth, flexShrink: 0, fontWeight: t.trackKey === selected ? 700 : 400 }}
                      >
                        {t.trackKey}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(100, (t.selfConsistency / 0.6) * 100))}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {t.selfConsistency.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Paper>
          </Stack>

          {selectedTrack && <TrackDetail track={selectedTrack} candidates={candidates} metric={metric} />}
        </Box>
      )}
    </Container>
  );
};
